import React, { useEffect, useRef, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import TopBar from "./TopBar";
import Navbar from "./Navbar";
import { restaurantInfo } from "../data/mock";

function SiteLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingScrollIdRef = useRef(null);

  const isHome = location.pathname === "/";
  const [isScrolled, setIsScrolled] = useState(
    !isHome || window.scrollY > 50,
  );
  const [topBarHidden, setTopBarHidden] = useState(
    window.scrollY > 0,
  );

  // Reset scroll + nav state on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
    // Non-home pages never show the TopBar — only the solid Navbar
    setTopBarHidden(!isHome);
    setIsScrolled(!isHome ? true : false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      // TopBar only hides/shows on the home page
      if (isHome) setTopBarHidden(scrollPosition > 0);
      setIsScrolled(!isHome ? true : scrollPosition > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  const scrollToId = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const navbarHeight = 128;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: elementPosition - navbarHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (
      location.pathname === "/" &&
      pendingScrollIdRef.current
    ) {
      const id = pendingScrollIdRef.current;
      pendingScrollIdRef.current = null;
      setTimeout(() => scrollToId(id), 50);
    }
  }, [location.pathname]);

  const requestHomeScroll = (id) => {
    if (location.pathname === "/") {
      scrollToId(id);
      return;
    }

    pendingScrollIdRef.current = id;
    navigate("/");
  };

  const handleHomeClick = () => {
    pendingScrollIdRef.current = null;
    if (!isHome) {
      navigate("/");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAboutClick = () => requestHomeScroll("about");
  const handleMenuClick = () =>
    requestHomeScroll("menu-section");
  const handleContactClick = () => requestHomeScroll("contact");

  const handleCateringClick = () => {
    pendingScrollIdRef.current = null;
    if (location.pathname !== "/catering") {
      navigate("/catering");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const contentPaddingTop = isHome
    ? topBarHidden
      ? "0"
      : "64px"
    : "128px";

  return (
    <div className="bg-white min-h-screen">
      {/* Single fixed wrapper — TopBar and Navbar are physically connected,
          eliminating any inter-element gap during the transition. */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div
          className={`overflow-hidden transition-[max-height] duration-150 ease-in-out ${
            topBarHidden ? "max-h-0" : "max-h-16"
          }`}
        >
          <TopBar
            phone={restaurantInfo.phone}
            socialLinks={restaurantInfo.social}
          />
        </div>

        <Navbar
          isScrolled={isScrolled}
          topBarHidden={topBarHidden}
          restaurantName={restaurantInfo.name}
          phone={restaurantInfo.phone}
          onHomeClick={handleHomeClick}
          onAboutClick={handleAboutClick}
          onMenuClick={handleMenuClick}
          onCateringClick={handleCateringClick}
          onContactClick={handleContactClick}
        />
      </div>

      <div
        className="transition-[padding-top] duration-150 ease-in-out"
        style={{ paddingTop: contentPaddingTop }}
      >
        <Outlet />
      </div>
    </div>
  );
}

export default SiteLayout;
